import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import { ConditionBuilder } from './ingestors/ConditionBuilder';
import type { ConditionNode, ScoringRule, ScoringRuleSplit } from './ingestors/types';

interface Team {
  id: string;
  name: string;
  slug: string;
}
interface Competency {
  id: string;
  name: string;
  slug: string;
  dimension: 'technical' | 'behavioral';
}

interface RuleForm {
  team_id: string;
  event_type: string;
  priority: number;
  splits: ScoringRuleSplit[];
  weight: number;
  enabled: boolean;
  conditions: ConditionNode | null;
  // M5.11: free-form description. Empty string when absent.
  description: string;
  // M5.13: per-rule scoring cooldown (ADR-0018). Default
  // to 0 / true so new rules match today's behaviour
  // (no cooldown, per-target key).
  cooldown_seconds: number;
  cooldown_use_target: boolean;
}

const emptyForm = (teamId: string): RuleForm => ({
  team_id: teamId,
  event_type: '',
  priority: 100,
  splits: [{ competency_id: '', xp: 5, coins: 0 }],
  weight: 1,
  enabled: true,
  conditions: null,
  description: '',
  // M5.13: defaults match the schema defaults: no cooldown,
  // per-target key when cooldown is set.
  cooldown_seconds: 0,
  cooldown_use_target: true,
});

export function ScoringRulesPage() {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });
  const competencies = useQuery({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Competency[] }>('/api/competencies'),
  });
  const eventTypes = useQuery({
    queryKey: ['event-types'],
    queryFn: () => ingestorsApi.listEventTypes(),
    staleTime: 60_000,
  });

  const [filterTeam, setFilterTeam] = useState<string>('');
  const rules = useQuery({
    queryKey: ['scoring-rules', filterTeam],
    queryFn: () => ingestorsApi.listRules(filterTeam || undefined),
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // v0.6.2: when true, the form is a duplicate of another
  // rule (pre-filled from the source rule's data). The save
  // path is the same as a regular create (POST a new rule
  // with a fresh id); only the seed data differs. Stored as
  // the source rule's id so the form header can show
  // 'New rule (duplicate of <source event>)' for context.
  const [duplicatingFrom, setDuplicatingFrom] = useState<string | null>(null);
  const [form, setForm] = useState<RuleForm | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => ingestorsApi.createRule({
      team_id: form!.team_id,
      priority: form!.priority,
      event_type: form!.event_type,
      conditions: form!.conditions,
      splits: form!.splits.filter((s) => s.competency_id && s.xp !== 0),
      weight: form!.weight,
      enabled: form!.enabled,
      description: form!.description.trim() || null,
      // M5.13: include cooldown fields. Sending both
      // fields explicitly so what the user sees is what
      // the api receives.
      cooldown_seconds: form!.cooldown_seconds,
      cooldown_use_target: form!.cooldown_use_target,
    }),
    onSuccess: () => {
      setCreating(false);
      setForm(null);
      setDuplicatingFrom(null);
      qc.invalidateQueries({ queryKey: ['scoring-rules'] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ScoringRule> }) =>
      ingestorsApi.updateRule(id, body),
    onSuccess: () => {
      setEditing(null);
      setForm(null);
      qc.invalidateQueries({ queryKey: ['scoring-rules'] });
    },
  });
  // Which rule is awaiting an "are you sure". A bare x used to delete on the
  // first click, sitting next to Edit and Duplicate in a dense table, and these
  // rows drive every award the platform makes. The endpoint is a hard
  // DELETE FROM scoring_rule - no way back.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => ingestorsApi.deleteRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scoring-rules'] }),
  });
  const reseed = useMutation({
    mutationFn: (teamId: string) => ingestorsApi.reseedTeamRules(teamId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scoring-rules'] }),
  });

  const sortedRules = useMemo(
    () => (rules.data?.rules ?? []).slice().sort((a, b) => a.priority - b.priority),
    [rules.data],
  );

  const startEdit = (r: ScoringRule) => {
    setEditing(r.id);
    setCreating(false);
    setDuplicatingFrom(null);
    setForm({
      team_id: r.team_id,
      event_type: r.event_type,
      priority: r.priority,
      splits: r.splits.length > 0 ? r.splits.map((s) => ({ ...s, coins: s.coins ?? 0 })) : [{ competency_id: '', xp: 0, coins: 0 }],
      weight: r.weight,
      enabled: r.enabled,
      conditions: r.conditions,
      // M5.11: hydrate the description input. null on the
      // server → empty string in the form (placeholder shows
      // the 'No description' hint text).
      description: r.description ?? '',
      // M5.13: prefill cooldown fields from the loaded rule.
      // Migration 0035 sets defaults so older rules hydrate
      // as 0 / true (no cooldown, per-target key).
      cooldown_seconds: r.cooldown_seconds,
      cooldown_use_target: r.cooldown_use_target,
    });
  };

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setDuplicatingFrom(null);
    setForm(emptyForm(filterTeam || teams.data?.teams?.[0]?.id || ''));
  };

  // v0.6.2: 'Duplicate' — pre-fill the create form from an
  // existing rule. The user picks a different team and
  // tweaks anything else before hitting Save, which POSTs
  // a new rule (server assigns a fresh id). The original
  // rule is untouched.
  //
  // Team default: the current filter if one is set
  // (common case: 'I want this rule on team X like the
  // one I just saw on team Y'); falls back to the source
  // rule's own team (common case: 'same team, slightly
  // different event/priority'); falls back to whatever
  // teams[0] is.
  const startDuplicate = (r: ScoringRule) => {
    setCreating(true);
    setEditing(null);
    setDuplicatingFrom(r.id);
    const destTeam =
      filterTeam ||
      r.team_id ||
      teams.data?.teams?.[0]?.id ||
      '';
    setForm({
      team_id: destTeam,
      event_type: r.event_type,
      // Slightly lower priority than the source so the
      // new rule doesn't pre-empt the source when both
      // teams are merged in the future. The operator can
      // bump it back up; the safer default wins.
      priority: r.priority + 1,
      splits: r.splits.length > 0
        ? r.splits.map((s) => ({ ...s, coins: s.coins ?? 0 }))
        : [{ competency_id: '', xp: 0, coins: 0 }],
      weight: r.weight,
      enabled: r.enabled,
      conditions: r.conditions,
      description: r.description ?? '',
      // M5.13: copy the cooldown config too, so the
      // duplicate lands on the destination team with
      // the same anti-abuse settings. The operator can
      // override after creating.
      cooldown_seconds: r.cooldown_seconds,
      cooldown_use_target: r.cooldown_use_target,
    });
  };

  const movePriority = async (r: ScoringRule, dir: -1 | 1) => {
    const sorted = sortedRules;
    const idx = sorted.findIndex((x) => x.id === r.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx]!;
    const b = sorted[swap]!;
    await Promise.all([
      update.mutateAsync({ id: a.id, body: { priority: b.priority } }),
      update.mutateAsync({ id: b.id, body: { priority: a.priority } }),
    ]);
  };

  const save = async () => {
    if (!form) return;
    setErr(null);
    const cleanSplits = form.splits.filter((s) => s.competency_id);
    if (creating && cleanSplits.length === 0) {
      setErr('Add at least one competency award.');
      return;
    }
    try {
      if (creating) {
        await create.mutateAsync();
      } else if (editing) {
        // M5.11: send description explicitly even on
        // empty (PATCH treats absent as 'no change';
        // empty string means 'set to empty', which is
        // the user's way of clearing the description).
        const trimmed = form.description.trim();
        await update.mutateAsync({
          id: editing,
          body: {
            priority: form.priority,
            event_type: form.event_type,
            conditions: form.conditions,
            splits: cleanSplits,
            weight: form.weight,
            enabled: form.enabled,
            description: trimmed === '' ? '' : trimmed,
            // M5.13: include cooldown config so PATCH
            // saves the form's current state. Sending
            // both fields explicitly is simpler than
            // "PATCH = only if changed" — they're small
            // ints and booleans.
            cooldown_seconds: form.cooldown_seconds,
            cooldown_use_target: form.cooldown_use_target,
          },
        });
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed.');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Scoring rules</h1>
          <p className="text-ink-muted">
            First matching rule wins (sorted by priority ascending). A rule can award XP to multiple competencies. Defaults seed on first boot per team.
          </p>
        </div>
        <button className="btn-primary" onClick={startCreate} disabled={creating || editing !== null || duplicatingFrom !== null}>
          + Add rule
        </button>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="label" htmlFor="team-filter">Team</label>
          <select
            id="team-filter"
            className="input"
            value={filterTeam}
            onChange={(e) => { setFilterTeam(e.target.value); setForm(null); setEditing(null); setCreating(false); setDuplicatingFrom(null); }}
          >
            <option value="">All teams</option>
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        {filterTeam ? (
          <button
            className="btn"
            onClick={async () => {
              setErr(null);
              try {
                await reseed.mutateAsync(filterTeam);
              } catch (e) {
                setErr(e instanceof ApiError ? e.message : 'Reseed failed.');
              }
            }}
            disabled={reseed.isPending}
            title="Reseed default rules (refuses if the team already has rules)"
          >
            {reseed.isPending ? 'Reseeding…' : 'Reseed defaults'}
          </button>
        ) : null}
      </div>

      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

      {form ? (
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
            {creating
              ? duplicatingFrom
                ? 'New rule (duplicate)'
                : 'New rule'
              : 'Edit rule'}
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="label">Team</label>
              <select
                className="input"
                value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                disabled={!!editing}
              >
                {(teams.data?.teams ?? []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="label">Event type</label>
              <select
                className="input font-mono"
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                required
              >
                <option value="">— select event type —</option>
                {(eventTypes.data?.event_types ?? []).map((t) => (
                  <option key={t.id} value={t.id}>{t.id}{t.description ? ` — ${t.description}` : ''}</option>
                ))}
              </select>
              <p className="text-xs text-ink-dim">
                Event types are managed in the {' '}
                <a href="/admin/event-types" className="underline">Event types</a> registry.
              </p>
            </div>
            <div className="space-y-1">
              <label className="label">Priority</label>
              <input
                className="input"
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
              />
            </div>
            <div className="space-y-1">
              <label className="label">Weight</label>
              <input
                className="input"
                type="number"
                step="0.1"
                min="0"
                max="99.99"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>
          </div>

          {/* Awards (splits) editor — one rule can award multiple
              competencies. Each split now also has a parallel
              coin credit (M5.8, ADR-0015 §2). XP and coins are
              both per-split; weight is applied to both. Set
              coins to 0 (or leave it blank) for 'no coins' —
              legacy rules with no coins key work the same way. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="label">Awards (competency + XP + 🪙)</label>
              <button
                type="button"
                className="btn"
                onClick={() => setForm({ ...form, splits: [...form.splits, { competency_id: '', xp: 0, coins: 0 }] })}
              >
                + Add award
              </button>
            </div>
            <p className="text-xs text-ink-dim">
              Each award grants XP <em>and</em> coins to a competency when this rule matches. Set coins to 0 to skip the coin credit. Add multiple to reward one event across several competencies.
            </p>
            {form.splits.map((s, i) => (
              <div key={i} className="flex gap-2">
                <select
                  className="input flex-1"
                  value={s.competency_id}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...s, competency_id: e.target.value };
                    setForm({ ...form, splits: next });
                  }}
                >
                  <option value="">— select competency —</option>
                  {(competencies.data?.competencies ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.dimension})</option>
                  ))}
                </select>
                <input
                  className="input w-20"
                  type="number"
                  value={s.xp}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...s, xp: Number(e.target.value) };
                    setForm({ ...form, splits: next });
                  }}
                  placeholder="XP"
                  title="XP awarded to this competency"
                />
                <input
                  className="input w-20"
                  type="number"
                  min={0}
                  value={s.coins ?? 0}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...s, coins: Number(e.target.value) };
                    setForm({ ...form, splits: next });
                  }}
                  placeholder="🪙"
                  title="Coins awarded to this player (M5.8). 0 = no coins."
                />
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => {
                    const next = form.splits.filter((_, j) => j !== i);
                    setForm({ ...form, splits: next.length > 0 ? next : [{ competency_id: '', xp: 0, coins: 0 }] });
                  }}
                  disabled={form.splits.length <= 1}
                  title="Remove award"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div>
            <label className="label">Conditions</label>
            <ConditionBuilder value={form.conditions} onChange={(c) => setForm({ ...form, conditions: c })} />
          </div>

          {/*
            M5.11: free-form description. Shows up in the list
            view as muted text below the event so multiple rules
            on the same event_type don't look identical at a
            glance. Capped at 500 chars in the UI (the schema
            is more permissive at 2000; the UI cap is the
            functional limit for human-written descriptions).
            Empty (or all-whitespace) shows as 'No description'
            in the list view; the placeholder text here makes
            it obvious.
          */}
          <div className="space-y-1">
            <label className="label" htmlFor="rule-description">
              Description (optional)
            </label>
            <textarea
              id="rule-description"
              className="input min-h-[4rem]"
              maxLength={500}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="A short note so this rule is identifiable in the list. E.g. 'External contributors only' or 'Mentored PRs (reviewed by senior dev)'."
            />
            <p className="text-xs text-ink-dim">
              {form.description.length} / 500. Shown next to the event in the rule list.
              Clear it by deleting the text and saving.
            </p>
          </div>
          {/*
            M5.13: per-rule scoring cooldown (ADR-0018).
            Two fields: cooldown_seconds (0 = no cooldown
            / legacy behaviour) and cooldown_use_target
            (per-target vs flat-mode key shape). The
            fields are below the description so the natural
            reading order is "what does this rule do?" →
            "how often can it fire?". Operators tune loud
            rules first (e.g. issue.commented on a chatty
            team gets cooldown_seconds = 60).
          */}
          <div className="space-y-1">
            <label className="label" htmlFor="rule-cooldown-seconds">
              Cooldown (seconds, per-user-per-rule)
            </label>
            <input
              id="rule-cooldown-seconds"
              type="number"
              min="0"
              max="86400"
              step="1"
              className="input w-32 tabular-nums"
              value={form.cooldown_seconds}
              onChange={(e) => setForm({ ...form, cooldown_seconds: Math.max(0, Math.min(86400, Number(e.target.value) || 0)) })}
            />
            <p className="text-xs text-ink-dim">
              0 = no cooldown (a player can earn XP from this rule on every event).
              Otherwise the rule can fire at most once per this many seconds per user.
              Default 0.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="rule-cooldown-use-target"
              type="checkbox"
              checked={form.cooldown_use_target}
              onChange={(e) => setForm({ ...form, cooldown_use_target: e.target.checked })}
            />
            <label className="text-sm" htmlFor="rule-cooldown-use-target">
              Cooldown is per-target (per Jira ticket, per PR)
            </label>
          </div>
          <p className="-mt-1 text-xs text-ink-dim">
            When checked, different tickets / PRs bypass the cooldown within the
            same window. When unchecked, the cooldown is global per-user regardless
            of target. Default checked. Only meaningful when the cooldown above is &gt; 0.
          </p>
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={() => { setForm(null); setEditing(null); setCreating(false); setDuplicatingFrom(null); }}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={create.isPending || update.isPending || !form.event_type}>
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Pri</th>
              <th>Event</th>
              <th>Team</th>
              <th>Awards</th>
              <th className="py-2 w-px whitespace-nowrap" aria-label="Enabled" title="Enabled">
                {/*
                  v0.6.2: no visible column header text — the
                  circle's colour (green/red) carries the
                  state, the symbol (✓/×) carries the meaning.
                  A label is still in the DOM (aria-label +
                  title above) for screen readers and hover.
                  Saves ~3rem of horizontal space, which matters
                  when the table has 6 columns and several
                  contain long descriptions.
                 */}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedRules.map((r, idx) => {
              const team = (teams.data?.teams ?? []).find((t) => t.id === r.team_id);
              return (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="py-2 font-mono">{r.priority}</td>
                  <td>
                    <div className="font-mono text-xs">{r.event_type}</div>
                    {/*
                      M5.11: show description (or a muted
                      placeholder) below the event. The
                      description is what makes the rule
                      list scannable; without it, two rules
                      on the same event_type look identical.
                    */}
                    {r.description ? (
                      <div
                        className="mt-0.5 max-w-md truncate text-xs text-ink-muted"
                        title={r.description}
                      >
                        {r.description}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-xs italic text-ink-dim">
                        No description
                      </div>
                    )}
                  </td>
                  <td className="text-ink-muted">{team?.name ?? r.team_id}</td>
                  <td>
                    {r.splits.length === 0 ? (
                      <span className="text-ink-dim">— no awards —</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {r.splits.map((s, i) => {
                          const comp = (competencies.data?.competencies ?? []).find((c) => c.id === s.competency_id);
                          // M5.8: surface the parallel coin
                          // credit alongside XP. We only show
                          // the coin value when it's non-zero
                          // (legacy rules with no coins key
                          // and rules that explicitly opt out
                          // both end up as 0; the "→ 0 🪙"
                          // is noise that hides the rules
                          // that DO credit coins). Admins can
                          // verify "no coins" in the edit form.
                          const coinStr = (s.coins ?? 0) > 0
                            ? ` + ${s.coins} 🪙`
                            : '';
                          return (
                            <li key={i} className="text-xs">
                              <span className="font-semibold">{s.xp} XP{coinStr}</span>
                              {' → '}
                              <span className="text-ink-muted">{comp?.name ?? s.competency_id}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </td>
                  <td>
                    {/*
                      v0.6.2: compact enabled indicator. Was a
                      plain '✓' / 'off' pair, which forced the
                      column header to be wide enough to read
                      the 'off' label and made the row scan
                      noisy. Replaced with a single coloured
                      circle containing the symbol: solid
                      green for enabled, hollow red for
                      disabled. The header label 'Enabled' is
                      enough context; the colour carries the
                      state, the symbol carries the meaning.
                      Tooltip gives the long form for screen
                      readers / hover.
                     */}
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs leading-none ${
                        r.enabled
                          ? 'bg-accent-success text-bg'
                          : 'border border-accent-danger/60 text-accent-danger'
                      }`}
                      title={r.enabled ? 'Enabled' : 'Disabled'}
                      aria-label={r.enabled ? 'Enabled' : 'Disabled'}
                    >
                      {r.enabled ? '✓' : '×'}
                    </span>
                  </td>
                  <td className="space-x-1 text-right">
                    <button
                      className="btn"
                      onClick={() => movePriority(r, -1)}
                      disabled={idx === 0 || !!editing || !!creating}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn"
                      onClick={() => movePriority(r, +1)}
                      disabled={idx === sortedRules.length - 1 || !!editing || !!creating}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button className="btn" onClick={() => startEdit(r)} disabled={!!editing || !!creating}>Edit</button>
                    <button
                      className="btn"
                      onClick={() => startDuplicate(r)}
                      disabled={!!editing || !!creating}
                      title="Duplicate this rule (pre-fills a new rule with the same event/conditions/awards/splits). Change the team dropdown before saving to copy across teams."
                    >
                      Duplicate
                    </button>
                    {confirmingDeleteId === r.id ? (
                      <>
                        {/* Names the rule: "are you sure?" with no name is a
                            question you can answer without reading the row. */}
                        <span className="mr-2 text-xs text-red-300" title={r.description ?? undefined}>
                          Delete {r.event_type}?
                        </span>
                        <button
                          className="btn-danger"
                          type="button"
                          onClick={() => {
                            remove.mutate(r.id);
                            setConfirmingDeleteId(null);
                          }}
                        >
                          Yes, delete
                        </button>
                        <button
                          className="btn ml-2"
                          type="button"
                          onClick={() => setConfirmingDeleteId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-danger"
                        type="button"
                        title="Delete this scoring rule"
                        onClick={() => setConfirmingDeleteId(r.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!sortedRules.length ? (
              <tr>
                <td colSpan={6} className="py-4 text-center text-ink-muted">No rules for this filter.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}